import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { generateMedicationSupportStack } from '../api/medications';
import { computeCanonicalHash } from '../utils/hash';
import { isSystemStackPinned, SYSTEM_STACK_TYPES } from './systemStackPrefs';

/**
 * Service for managing medication support stack generation and polling
 */
export class MedicationStackService {
  
  /**
   * Generate medication support stack
   * @param {Array} medications - User medications array
   * @returns {Promise<boolean>} - Success status
   */
  static async generateStack(medications) {
    try {
      const user = getAuth().currentUser;
      if (!user) {
        console.warn('No authenticated user for stack generation');
        return false;
      }

      const authToken = await user.getIdToken();
      await generateMedicationSupportStack(authToken, medications);
      console.log('🚀 [MedicationStackService] Stack generation triggered successfully');
      return true;
    } catch (error) {
      console.error('❌ [MedicationStackService] Failed to generate medication support stack:', error);
      return false;
    }
  }

  /**
   * Get user's medication profile update time
   * @returns {Promise<Date|null>} - Last update time or null
   */
  static async getMedicationProfileUpdateTime() {
    try {
      const user = getAuth().currentUser;
      if (!user) return null;

      const medProfileDoc = await getDoc(doc(db, 'med_profile', user.uid));
      if (!medProfileDoc.exists()) return null;

      const data = medProfileDoc.data();
      const updatedAt = data.updatedAt;
      
      if (!updatedAt) return null;
      
      // Handle both ISO string and Firestore timestamp
      return updatedAt instanceof Date ? updatedAt : new Date(updatedAt);
    } catch (error) {
      console.error('❌ [MedicationStackService] Failed to get medication profile update time:', error);
      return null;
    }
  }

  /**
   * Get medication support stack update time
   * @returns {Promise<{stack: Object|null, updateTime: Date|null}>}
   */
  static async getMedicationStackInfo() {
    try {
      const user = getAuth().currentUser;
      if (!user) return { stack: null, updateTime: null };

      // Check for medication support stack in system_stacks sub-collection
      const docPath = `users/${user.uid}/system_stacks/medication_support`;
      console.log('🔍 [MedicationStackService] Attempting to read from path:', docPath);
      const systemStackDoc = await getDoc(doc(db, 'users', user.uid, 'system_stacks', 'medication_support'));
      if (!systemStackDoc.exists()) {
        console.log('🔍 [MedicationStackService] No system stack document found');
        return { stack: null, updateTime: null };
      }

      const data = systemStackDoc.data();
      if (!data || !data.stackData) {
        console.log('🔍 [MedicationStackService] No stackData found in system stack document');
        return { stack: null, updateTime: null };
      }

      const updateTime = data.updatedAt?.toDate() || new Date();

      // The pin lives on users/{uid} (STACK-526); `data.isPinned` is only the
      // legacy location the fallback still honours.
      let userData = null;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        userData = userDoc.exists() ? userDoc.data() : null;
      } catch (e) {
        console.warn('⚠️ [MedicationStackService] Could not read pin preferences; falling back to the stack document');
      }

      const stack = {
        id: `system_medication_support_${user.uid}`,
        ...data.stackData,
        isSystemGenerated: true,
        systemStackType: 'medication_support',
        isPinned: isSystemStackPinned(userData, SYSTEM_STACK_TYPES.MEDICATION, data),
        jsCreatedAt: data.createdAt?.toDate() || new Date(),
        jsUpdatedAt: updateTime
      };
      // Freshness check: only return if source_meds_hash matches current meds hash
      try {
        const meds = await this.getUserMedications();
        const currentHash = await computeCanonicalHash(meds || []);
        if (data.source_meds_hash && data.source_meds_hash !== currentHash) {
          console.log('🔍 [MedicationStackService] Stack is stale relative to current meds; hiding');
          return { stack: null, updateTime: null };
        }
      } catch (e) {
        console.warn('⚠️ [MedicationStackService] Freshness check failed; returning stack anyway');
      }

      console.log('🔍 [MedicationStackService] Found system stack with update time:', updateTime);
      return { stack, updateTime };
    } catch (error) {
      console.error('❌ [MedicationStackService] Failed to get medication stack info:', error);
      return { stack: null, updateTime: null };
    }
  }

  /**
   * Check if a medication support stack exists
   * @returns {Promise<boolean>} - True if stack exists
   */
  static async stackExists() {
    try {
      const { stack } = await this.getMedicationStackInfo();
      return !!stack;
    } catch (error) {
      console.error('❌ [MedicationStackService] Failed to check if stack exists:', error);
      return false;
    }
  }

  /**
   * Check if medication list is newer than the support stack
   * @returns {Promise<boolean>} - True if medications are newer
   */
  static async isMedicationListNewer() {
    try {
      const [medicationUpdateTime, { updateTime: stackUpdateTime }] = await Promise.all([
        this.getMedicationProfileUpdateTime(),
        this.getMedicationStackInfo()
      ]);

      // If no medication profile exists, not newer
      if (!medicationUpdateTime) {
        console.log('🔍 [MedicationStackService] No medication profile found');
        return false;
      }
      
      // If no stack exists, medications are "newer" (should generate)
      if (!stackUpdateTime) {
        console.log('🔍 [MedicationStackService] No stack exists, medications are "newer"');
        return true;
      }

      // Compare timestamps (add 5 second buffer to account for processing time)
      const bufferMs = 5000;
      const isNewer = medicationUpdateTime.getTime() > (stackUpdateTime.getTime() + bufferMs);
      console.log('🔍 [MedicationStackService] Medication time:', medicationUpdateTime, 'Stack time:', stackUpdateTime, 'Is newer:', isNewer);
      return isNewer;
    } catch (error) {
      console.error('❌ [MedicationStackService] Failed to compare update times:', error);
      return false;
    }
  }

  /**
   * Poll for updated medication support stack
   * @param {Function} onUpdate - Callback when stack is updated
   * @param {Function} onTimeout - Callback when polling times out
   * @param {number} timeoutMs - Timeout in milliseconds (default 30000)
   * @param {number} intervalMs - Polling interval in milliseconds (default 2000)
   * @returns {Function} - Cleanup function to stop polling
   */
  static pollForStackUpdate(onUpdate, onTimeout, timeoutMs = 30000, intervalMs = 2000) {
    let timeoutId;
    let intervalId;
    let isPolling = true;

    console.log('🔄 [MedicationStackService] Starting to poll for stack updates');

    // Set up timeout
    timeoutId = setTimeout(() => {
      if (isPolling) {
        console.log('⏰ [MedicationStackService] Polling timeout reached');
        isPolling = false;
        clearInterval(intervalId);
        onTimeout();
      }
    }, timeoutMs);

    // Set up polling interval
    const checkForUpdate = async () => {
      if (!isPolling) return;

      try {
        const isNewer = await this.isMedicationListNewer();
        console.log('🔍 [MedicationStackService] Checking if medications are newer:', isNewer);
        
        if (!isNewer) {
          // Stack has been updated!
          console.log('✅ [MedicationStackService] Stack has been updated');
          isPolling = false;
          clearTimeout(timeoutId);
          clearInterval(intervalId);
          
          // Get the updated stack info
          const { stack } = await this.getMedicationStackInfo();
          onUpdate(stack);
        }
      } catch (error) {
        console.error('❌ [MedicationStackService] Error during polling:', error);
      }
    };

    // Start polling immediately, then continue at intervals
    checkForUpdate();
    intervalId = setInterval(checkForUpdate, intervalMs);

    // Return cleanup function
    return () => {
      console.log('🛑 [MedicationStackService] Stopping polling');
      isPolling = false;
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }

  /**
   * Get user medications
   * @returns {Promise<Array>} - User medications array
   */
  static async getUserMedications() {
    try {
      const user = getAuth().currentUser;
      if (!user) return [];

      const medProfileDoc = await getDoc(doc(db, 'med_profile', user.uid));
      if (!medProfileDoc.exists()) return [];

      const data = medProfileDoc.data();
      return data.medications || [];
    } catch (error) {
      console.error('❌ [MedicationStackService] Failed to get user medications:', error);
      return [];
    }
  }
} 